import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — итальянский текст
const lang = 'it';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        Un evento si crea in un solo passaggio con il modulo «Crea nuovo evento». Scegli prima il tipo di evento
        e il modo in cui le gare entrano nell'evento: da questo dipende quali campi mostra il modulo. Qui sotto
        trovi ogni passaggio e ogni variante con gli screenshot.
      </p>

      <Toc
        title="Indice"
        items={[
          ['access', 'Prima di iniziare'],
          ['choose', 'Scegliere la variante dell\'evento'],
          ['basics', 'Passo 1. Base: nome, descrizione, co-organizzatori'],
          ['type', 'Passo 2. Tipo di evento'],
          ['status', 'Passo 3. Stato e immagine'],
          ['registration', 'Passo 4. Iscrizioni e visibilità'],
          ['scoring', 'Passo 5. Classifiche e punti'],
          ['wildcards', 'Passo 6. Wild card (gara e torneo)'],
          ['sync', 'Variante A. Gare tramite modello di ricerca'],
          ['manual', 'Variante B. Gare di altri utenti ROUVY'],
          ['open', 'Variante C. Torneo aperto'],
          ['official', 'Variante D. Evento ufficiale'],
          ['after', 'Dopo la creazione: modifica e co-organizzatori'],
          ['problems', 'Errori frequenti']
        ]}
      />

      <section id="access">
        <h2>Prima di iniziare</h2>
        <ul>
          <li>
            Il pulsante <strong>«Crea evento»</strong> <Num>1</Num> nella pagina «Eventi» è visibile solo agli account
            autorizzati a creare eventi. Se non lo vedi, contatta un amministratore.
          </li>
          <li>Il pulsante <strong>«Tipi di evento»</strong> <Num>2</Num> apre una breve guida ai tipi.</li>
          <li>
            Il tipo <strong>«Torneo aperto»</strong> è disponibile solo per gli account con accesso speciale. Gli altri
            non lo vedono nell'elenco dei tipi.
          </li>
          <li>Perché le gare vengano trovate con il modello di ricerca, l'autore dell'evento o un co-organizzatore deve avere ROUVY collegato.</li>
        </ul>
        <Shot
          name="events-list"
          alt="Pagina «Eventi»: pulsanti «Tipi di evento» e «Crea evento»"
          caption="La pagina «Eventi». L'icona della matita sulla riga dell'evento (3) apre la modifica: la vedono l'autore dell'evento e gli amministratori."
        />
      </section>

      <section id="choose">
        <h2>Scegliere la variante dell'evento</h2>
        <p>La variante nasce da due scelte: il <strong>tipo</strong> e <strong>come si aggiungono le gare</strong>.</p>

        <h3>Tipi di evento</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Percorsi</th>
                <th>Calendario</th>
                <th>Limite gare per tappa</th>
                <th>Wild card</th>
                <th>Quando usarlo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Uscita di gruppo</strong></td>
                <td>1</td>
                <td>Sì</td>
                <td>—</td>
                <td>—</td>
                <td>Un giro in compagnia o un allenamento con gli amici</td>
              </tr>
              <tr>
                <td><strong>Gara</strong></td>
                <td>1</td>
                <td>Sì</td>
                <td>Sì</td>
                <td>Sì</td>
                <td>Una singola gara, anche con più partenze</td>
              </tr>
              <tr>
                <td><strong>Torneo</strong></td>
                <td>1 o più</td>
                <td>Sì</td>
                <td>Sì</td>
                <td>Sì</td>
                <td>Una serie di tappe su percorsi diversi</td>
              </tr>
              <tr>
                <td><strong>Torneo aperto</strong></td>
                <td>1 o più</td>
                <td>No, solo un periodo</td>
                <td>—</td>
                <td>—</td>
                <td>Ogni partecipante corre i percorsi quando vuole; conta il tempo migliore</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">«Percorsi»: quanti percorsi ROUVY diversi può avere l'evento. Un percorso può avere più partenze.</p>

        <h3>Modi di aggiungere le gare</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Modo</th>
                <th>Chi crea le gare su ROUVY</th>
                <th>Tipi di evento</th>
                <th>Come attivarlo</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>A. Modello di ricerca</strong></a></td>
                <td>L'autore dell'evento o i co-organizzatori</td>
                <td>Uscita di gruppo, gara, torneo</td>
                <td>Predefinito</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>B. A mano</strong></a></td>
                <td>Qualsiasi utente ROUVY</td>
                <td>Uscita di gruppo, gara, torneo</td>
                <td>Casella «Gare create da altri utenti ROUVY»</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>C. Percorsi senza calendario</strong></a></td>
                <td>Ogni partecipante per sé</td>
                <td>Torneo aperto</td>
                <td>Scegliere il tipo «Torneo aperto»</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>D. Evento ufficiale</strong></a></td>
                <td>ROUVY</td>
                <td>Tutti tranne il torneo aperto</td>
                <td>Stato «Ufficiale»</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          Il modo di aggiungere le gare si sceglie alla creazione e poi non si può cambiare. Se hai sbagliato,
          crea di nuovo l'evento.
        </Note>
      </section>

      <section id="basics">
        <h2>Passo 1. Base: nome, descrizione, co-organizzatori</h2>
        <Shot name="create-basics" alt="Parte alta del modulo di creazione dell'evento" caption="Parte alta del modulo per la variante con modello di ricerca" />
        <ol>
          <li><Num>1</Num> <strong>Nome dell'evento</strong>: obbligatorio. Senza modello di ricerca, le gare ROUVY vengono cercate con questo nome.</li>
          <li>
            <Num>2</Num> <strong>Gare create da altri utenti ROUVY</strong>: attivala solo per la <a href="#manual">variante B</a>.
            Il campo del modello scompare e compare un elenco di gare.
          </li>
          <li>
            <Num>3</Num> <strong>Modello di ricerca Rouvy</strong>: la parte del nome comune a tutte le tue gare ROUVY di questo evento,
            ad esempio <code>Autumn Cup</code>. Vedi la <a href="#sync">variante A</a>.
          </li>
          <li><Num>4</Num> <strong>Co-organizzatori</strong>: utenti che possono a loro volta aggiungere gare. Anche le loro gare ROUVY vengono trovate dal modello.</li>
          <li><Num>5</Num> <strong>Descrizione</strong>: regole, formato, link. I partecipanti la vedono nella pagina dell'evento.</li>
        </ol>

        <h3>Aggiungere un co-organizzatore</h3>
        <p>Inizia a scrivere un nome o un nickname (almeno 2 caratteri) e scegli la persona dall'elenco. I selezionati compaiono come etichette; la croce li rimuove.</p>
        <Shot name="co-organizers" alt="Ricerca dei co-organizzatori" caption="Un co-organizzatore è già aggiunto, un secondo viene scelto dall'elenco" />
      </section>

      <section id="type">
        <h2>Passo 2. Tipo di evento</h2>
        <p>Sotto l'elenco dei tipi il modulo mostra un suggerimento sul tipo scelto. Il tipo decide quali campi seguono.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Tipo Uscita di gruppo" caption="Uscita di gruppo: un percorso, senza limite di gare né wild card" />
          <Shot name="type-race" alt="Tipo Gara" caption="Gara: un percorso, aggiunge gare per tappa e wild card" />
          <Shot name="type-tournament" alt="Tipo Torneo" caption="Torneo: più percorsi, stessi campi extra della gara" />
          <Shot name="type-open_tournament" alt="Tipo Torneo aperto" caption="Torneo aperto: aggiunge il periodo del torneo" />
        </div>
      </section>

      <section id="status">
        <h2>Passo 3. Stato e immagine</h2>
        <Shot name="status-image" alt="Stato e immagine dell'evento" />
        <ol>
          <li>
            <Num>1</Num> <strong>Stato</strong>: «Personalizzato» va bene quasi sempre. «Ufficiale» è per gli eventi ufficiali ROUVY,
            vedi la <a href="#official">variante D</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Immagine</strong>: la copertina dell'evento. Usa «Carica immagine», fino a 5 MB.
            «Rimuovi immagine» cancella l'immagine scelta.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Passo 4. Iscrizioni e visibilità</h2>
        <Shot name="registration" alt="Impostazioni delle iscrizioni" caption="Un torneo: «Gare per tappa» esiste solo per gare e tornei" />
        <ol>
          <li>
            <Num>1</Num> <strong>Chiusura iscrizioni (ore prima della partenza)</strong>: quante ore prima della prima gara
            si chiudono le iscrizioni. Vuoto: ci si può iscrivere anche dopo la partenza.
          </li>
          <li>
            <Num>2</Num> <strong>Gare per tappa</strong> (gara e torneo): quante gare di una tappa un partecipante può correre per la classifica.
            Il partecipante si iscrive a gare precise e contano solo quelle. Vuoto: contano tutte.
          </li>
          <li><Num>3</Num> <strong>Evento lungo</strong>: le iscrizioni restano aperte per tutto il tempo, anche dopo tutte le gare.</li>
          <li>
            <Num>4</Num> <strong>Evento nascosto</strong>: nell'elenco degli eventi lo vedono solo amministratori e organizzatori.
            I risultati si calcolano normalmente e il link diretto funziona per tutti.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Passo 5. Classifiche e punti</h2>
        <Shot name="scoring" alt="Classifiche e punti" caption="La classifica a squadre è attiva: sono comparsi i campi della squadra" />
        <ol>
          <li><Num>1</Num> <strong>Classifica individuale</strong> e/o <strong>Classifica a squadre</strong>. Possono essere attive entrambe.</li>
          <li><Num>2</Num> <strong>Punti per il 1° posto</strong>: quanto riceve il vincitore della tappa (1000 di default).</li>
          <li><Num>3</Num> <strong>Scarto di punti</strong>: quanto meno riceve ogni posizione successiva (10 di default).</li>
          <li><Num>4</Num> <strong>Componenti della squadra conteggiati</strong> (solo a squadre): quanti migliori risultati della squadra contano.</li>
          <li>
            <Num>5</Num> <strong>Massimo componenti per squadra</strong> (solo a squadre): quanti corridori di una squadra possono iscriversi.
            Vuoto: nessun limite.
          </li>
          <li><Num>6</Num> <strong>Crea</strong>: salva l'evento e torna all'elenco degli eventi.</li>
        </ol>
        <Note>
          Punti per una posizione: <code>punti per il 1° posto − (posizione − 1) × scarto</code>, ma mai sotto 0.
          Con 1000 e 10: 1° — 1000, 2° — 990, 10° — 910, dal 101° in poi — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Passo 6. Wild card (gara e torneo)</h2>
        <p>Una wild card permette di correre una tappa fuori calendario. Il blocco esiste solo negli eventi personalizzati di tipo Gara e Torneo.</p>
        <ol>
          <li><strong>Wild card (numero massimo)</strong>: quante wild card un partecipante può usare nell'evento. Vuoto: nessuna wild card.</li>
          <li>
            <strong>Chi crea la wild card</strong> (compare con più di 0 wild card):
            <ul>
              <li><strong>Creata dall'organizzatore</strong>: il partecipante indica data e ora, l'organizzatore crea per lui una gara su ROUVY e approva la richiesta.</li>
              <li><strong>Creata dal partecipante</strong>: il partecipante crea una gara su ROUVY secondo i tuoi requisiti e invia il link; l'organizzatore approva o rifiuta.</li>
            </ul>
          </li>
          <li>
            <strong>Campo dei requisiti</strong> (solo «Creata dal partecipante»): cosa deve rispettare la gara del partecipante: percorso, impostazioni, orario.
            I partecipanti vedono questo testo quando inviano una richiesta.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Wild card create dall'organizzatore" caption="Creata dall'organizzatore" />
          <Shot name="wildcards-participant" alt="Wild card create dal partecipante" caption="Creata dal partecipante: compare il campo dei requisiti" />
        </div>
        <p>L'organizzatore riceve una notifica per ogni richiesta.</p>
      </section>

      <section id="sync">
        <h2>Variante A. Gare tramite modello di ricerca</h2>
        <p>È la variante predefinita. Tu (o i co-organizzatori) create le gare su ROUVY, e il sistema le trova con il modello e le aggiunge all'evento.</p>
        <ol className="guide-steps">
          <li>Crea le gare su ROUVY con una parte comune nel nome, ad esempio <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>Lascia la casella «Gare create da altri utenti ROUVY» <strong>disattivata</strong>.</li>
          <li>Scrivi la parte comune in <strong>«Modello di ricerca Rouvy»</strong>: <code>Autumn Cup</code>. Vuoto: si usa il nome dell'evento.</li>
          <li>Aggiungi i co-organizzatori se anche altri creano gare.</li>
          <li>Completa gli altri passaggi e premi <strong>«Crea»</strong>.</li>
          <li>La ricerca delle gare parte in background subito dopo la creazione. Le gare compaiono entro pochi minuti: controllale nella modifica dell'evento.</li>
        </ol>
        <p>Nella modifica dell'evento compaiono il blocco di sincronizzazione e l'elenco delle gare trovate:</p>
        <Shot name="edit-sync" alt="Sincronizzazione e gare aggiunte" />
        <ol>
          <li><Num>1</Num> <strong>Sincronizza percorsi</strong>: cerca di nuovo, ad esempio dopo aver creato nuove gare su ROUVY. Puoi chiudere la pagina.</li>
          <li><Num>2</Num> <strong>Aggiorna elenco</strong>: carica il risultato della sincronizzazione senza ricaricare la pagina.</li>
          <li><Num>3</Num> <strong>Elimina</strong>: toglie dall'evento una gara di troppo.</li>
        </ol>
        <Note>Se modifichi il nome dell'evento o il modello e salvi, la ricerca parte automaticamente.</Note>
      </section>

      <section id="manual">
        <h2>Variante B. Gare di altri utenti ROUVY</h2>
        <p>Se le gare ROUVY non le hai create tu né i co-organizzatori, la ricerca con il modello non le trova. In questo caso le gare si aggiungono a mano.</p>
        <ol className="guide-steps">
          <li>Attiva <strong>«Gare create da altri utenti ROUVY»</strong>. Il campo del modello scompare e compare il blocco «Gare dell'evento».</li>
          <li>
            Per ogni gara compila:
            <ul>
              <li><strong>Percorso ROUVY*</strong>: ID del percorso o link come <code>https://riders.rouvy.com/route/12345</code>;</li>
              <li><strong>Nome del percorso</strong>: facoltativo, se ne vuoi uno tuo;</li>
              <li><strong>Link alla gara ROUVY*</strong>: <code>https://riders.rouvy.com/events/…</code>;</li>
              <li><strong>Nome della gara</strong>: facoltativo; se vuoto, viene preso da ROUVY;</li>
              <li><strong>Data e ora di partenza*</strong>: nel tuo fuso orario.</li>
            </ul>
          </li>
          <li><strong>«Aggiungi gara»</strong> aggiunge una riga, <strong>«Rimuovi»</strong> la cancella. Le righe vuote non vengono salvate.</li>
          <li>Premi <strong>«Crea»</strong>. Se una gara non va a buon fine, l'evento non viene creato e l'errore indica il numero di riga.</li>
        </ol>
        <Shot name="manual-create" alt="Gare dell'evento alla creazione" caption="L'elenco delle gare alla creazione dell'evento" />
        <p>Puoi aggiungere gare anche dopo: nella modifica dell'evento c'è un modulo per una gara <Num>1</Num>. La gara aggiunta compare subito nell'elenco <Num>2</Num>.</p>
        <Shot name="edit-manual" alt="Aggiungere una gara nella modifica" />
      </section>

      <section id="open">
        <h2>Variante C. Torneo aperto</h2>
        <p>
          Un torneo aperto non ha calendario. Ogni partecipante crea su ROUVY una gara (Race) su un percorso del torneo e la corre quando vuole.
          L'uscita conta per percorso e tipo di gara, e in classifica va il miglior tempo tra tutti i tentativi.
        </p>
        <ol className="guide-steps">
          <li>In «Tipo di evento» scegli <strong>«Torneo aperto»</strong>.</li>
          <li>
            Imposta il <strong>periodo del torneo</strong>: «Inizio del torneo» e «Fine del torneo». Le uscite al di fuori non contano.
            Entrambi sono facoltativi: senza limiti il torneo non ha scadenza. La fine deve essere successiva all'inizio.
          </li>
          <li>Completa gli altri passaggi e premi <strong>«Crea»</strong>.</li>
          <li>Apri la modifica dell'evento e aggiungi i percorsi (vedi sotto).</li>
        </ol>
        <Note kind="warning">I percorsi di un torneo aperto si aggiungono solo dopo aver creato l'evento, nella modifica.</Note>
        <Shot name="edit-open" alt="Percorsi del torneo aperto" />
        <ol>
          <li><Num>1</Num> Incolla il link della pagina di un percorso ROUVY: <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Premi <strong>«Aggiungi percorso»</strong>.</li>
          <li><Num>3</Num> Il percorso entra nel torneo. «Elimina» lo toglie.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Variante D. Evento ufficiale</h2>
        <p>
          Lo stato <strong>«Ufficiale»</strong> è per gli eventi ufficiali ROUVY. Le loro gare le gestisce ROUVY, quindi il modulo è più corto:
          niente casella per le gare a mano, modello di ricerca, co-organizzatori e wild card.
        </p>
        <Shot name="official" alt="Modulo di un evento ufficiale" caption="Stato «Ufficiale»: i blocchi superflui sono nascosti" />
      </section>

      <section id="after">
        <h2>Dopo la creazione: modifica e co-organizzatori</h2>
        <ul>
          <li>La modifica si apre con l'icona della matita sulla riga dell'evento nella pagina «Eventi». Tutte le impostazioni le cambiano l'autore dell'evento e gli amministratori.</li>
          <li>Il modo di aggiungere le gare (la casella «Gare create da altri utenti ROUVY») non si può cambiare nella modifica.</li>
          <li>I co-organizzatori vedono una pagina semplificata: possono aggiungere gare (avviare la sincronizzazione o inserirle a mano) ed eliminare quelle che hanno aggiunto. Le altre impostazioni non sono disponibili per loro.</li>
        </ul>
        <Shot name="co-organizer-view" alt="Pagina del co-organizzatore" caption="Così un co-organizzatore vede l'evento: può eliminare solo la propria gara" />
      </section>

      <section id="problems">
        <h2>Errori frequenti</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Messaggio o situazione</th>
                <th>Causa e soluzione</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>«Il nome dell'evento è obbligatorio»</td>
                <td>Compila «Nome dell'evento».</td>
              </tr>
              <tr>
                <td>«Gli eventi Uscita di gruppo e Gara possono avere un solo percorso»</td>
                <td>L'evento ha gare su percorsi diversi. Scegli il tipo Torneo o togli le gare in più.</td>
              </tr>
              <tr>
                <td>«Il torneo deve finire dopo l'inizio»</td>
                <td>Correggi il periodo del torneo aperto.</td>
              </tr>
              <tr>
                <td>«Indica percorso, link alla gara ROUVY e data/ora di partenza per ogni gara»</td>
                <td>In una riga delle gare a mano manca un campo obbligatorio. Compilalo o rimuovi la riga.</td>
              </tr>
              <tr>
                <td>Il torneo aperto viene rifiutato al salvataggio</td>
                <td>L'account non ha accesso ai tornei aperti. Scegli un altro tipo.</td>
              </tr>
              <tr>
                <td>Nessuna gara dopo la sincronizzazione</td>
                <td>
                  Controlla che i nomi delle gare ROUVY contengano il modello, che le abbia create tu o un co-organizzatore
                  e che il tuo ROUVY sia collegato. Attendi qualche minuto e premi «Aggiorna elenco».
                  Se le gare le ha create qualcun altro, serve la <a href="#manual">variante B</a>: crea di nuovo l'evento.
                </td>
              </tr>
              <tr>
                <td>Manca il pulsante «Crea evento»</td>
                <td>L'account non può creare eventi: contatta un amministratore.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
